import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Building2, Wifi, Tv, Hotel, Stethoscope, Home as HomeIcon, Church, Briefcase, Landmark, Ship, GraduationCap, Network } from "lucide-react";
import { useI18n, pickLang } from "@/lib/I18nProvider";
import { INDUSTRIES } from "@/lib/i18n";

const ICONS = {
  isp: Wifi, telco: Network, cable: Tv, "ott-operators": Tv,
  hotel: Hotel, hospital: Stethoscope, residential: HomeIcon, church: Church,
  corporate: Briefcase, government: Landmark, maritime: Ship, campus: GraduationCap,
};

export default function Industries() {
  const { lang, t } = useI18n();
  const industries = pickLang(INDUSTRIES, lang);

  return (
    <div data-testid="industries-page">
      <section className="relative overflow-hidden bg-radial-glow">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="overline">{t("nav.industries")}</div>
          <h1 className="font-display mt-6 text-5xl md:text-7xl font-bold tracking-tighter leading-[0.95] max-w-4xl text-white">
            {lang === "fr" ? <>Conçu pour tous ceux qui <span className="gradient-text">diffusent.</span></>
            : lang === "es" ? <>Construido para quien <span className="gradient-text">emite.</span></>
            : lang === "pt" ? <>Construído para quem <span className="gradient-text">emite.</span></>
            : <>Built for whoever <span className="gradient-text">broadcasts.</span></>}
          </h1>
          <p className="mt-8 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">
            {lang === "fr" ? "12 verticales — des FAI et opérateurs aux hôtels, hôpitaux, églises et croisières. Chaque industrie a sa propre stack Push2Stream, prête à déployer."
            : lang === "es" ? "12 verticales — de ISPs y telcos a hoteles, hospitales, iglesias y cruceros. Cada industria tiene su propia stack Push2Stream, lista para desplegar."
            : lang === "pt" ? "12 verticais — de ISPs e telcos a hotéis, hospitais, igrejas e cruzeiros. Cada indústria tem a sua própria stack Push2Stream, pronta a implementar."
            : "12 verticals — from ISPs and telcos to hotels, hospitals, churches and cruise lines. Each industry gets its own Push2Stream stack, ready to deploy."}
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((ind, i) => {
              const Icon = ICONS[ind.slug] || Building2;
              return (
                <motion.div
                  key={ind.slug}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.45, delay: i * 0.04 }}
                  data-testid={`industry-card-${ind.slug}`}
                  className="card-hover glass rounded-2xl p-7 flex flex-col group"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="w-12 h-12 rounded-xl gradient-bg flex items-center justify-center text-black">
                      <Icon size={22} strokeWidth={2.2} />
                    </div>
                    <ArrowUpRight size={20} className="text-slate-500 group-hover:text-[#1FA8FF] transition-colors" />
                  </div>
                  <h3 className="font-display mt-6 text-2xl font-bold tracking-tight text-white">{ind.title}</h3>
                  <p className="mt-3 text-slate-300 leading-relaxed text-sm">{ind.blurb}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/10 bg-[#324567]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter text-white">
            {lang === "fr" ? <>Votre secteur n'est pas listé ? <span className="gradient-text">Parlons-en.</span></>
            : lang === "es" ? <><span className="gradient-text">¿No ves tu industria?</span> Hablemos.</>
            : lang === "pt" ? <><span className="gradient-text">Não vê a sua indústria?</span> Vamos falar.</>
            : <>Don't see your industry? <span className="gradient-text">Let's talk.</span></>}
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/demo" data-testid="industries-cta-demo" className="btn-primary">{t("cta.demo_short")} <ArrowRight size={18} /></Link>
            <Link to="/contact" data-testid="industries-cta-contact" className="btn-ghost">{t("cta.contact_sales")}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
